import { useCallback, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { authAtom, userInformationAtom } from '../Services/_states';
import useRefreshUserSession from '../hooks/useRefreshUserSession';
import { getSessionUserId } from './userSession';

const FOCUS_REFRESH_COOLDOWN_MS = 60_000;

/**
 * Keeps userInfo/rbac_grants fresh on app load and when the tab regains focus.
 * Profile permission edits apply without requiring logout.
 */
export default function UserSessionSync() {
  const auth = useRecoilValue(authAtom);
  const userInfo = useRecoilValue(userInformationAtom);
  const refreshUserSession = useRefreshUserSession();
  const refreshRef = useRef(refreshUserSession);
  refreshRef.current = refreshUserSession;
  const authRef = useRef(auth);
  const userInfoRef = useRef(userInfo);
  const inFlightRef = useRef(null);
  const lastRefreshAtRef = useRef(0);
  authRef.current = auth;
  userInfoRef.current = userInfo;

  const userId = getSessionUserId(auth, userInfo);
  const isAuthenticated = Boolean(auth?.token && userId);

  const refreshInBackground = useCallback((activeUserId, { force = false } = {}) => {
    if (!activeUserId) {
      return Promise.resolve(null);
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const now = Date.now();
    if (!force && now - lastRefreshAtRef.current < FOCUS_REFRESH_COOLDOWN_MS) {
      return Promise.resolve(null);
    }

    lastRefreshAtRef.current = now;
    const request = refreshRef.current(activeUserId)
      .catch((error) => {
        // Session refresh is best-effort. A transient error must not disturb
        // the current route, local form state, or authenticated UI.
        console.warn('Background session refresh failed:', error);
        return null;
      })
      .finally(() => {
        if (inFlightRef.current === request) {
          inFlightRef.current = null;
        }
      });

    inFlightRef.current = request;
    return request;
  }, []);

  // App boot / full page refresh — replace stale localStorage snapshot
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    refreshInBackground(userId, { force: true });
  }, [isAuthenticated, refreshInBackground, userId]);

  // Tab focus — pick up profile changes made in another tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      const activeAuth = authRef.current;
      const activeUserInfo = userInfoRef.current;
      if (document.visibilityState !== 'visible' || !activeAuth?.token) {
        return;
      }
      const activeUserId = getSessionUserId(activeAuth, activeUserInfo);
      if (activeUserId) {
        refreshInBackground(activeUserId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshInBackground]);

  return null;
}
